<!-- Lien d'evitement (accessibilite clavier) -->
<a class="skip-link" href="#main-content" i18n="@@layout.skipToContent"
  >تخطّي إلى المحتوى</a
>

<div
  class="app-shell"
  [class.is-rail]="estEnRail()"
  [class.is-hidden]="!compact() && sidebarMode() === 'hidden'"
  [class.is-compact]="compact()"
  [class.sans-transition]="transitionsGelees()"
>
  <!-- ====================== TOPBAR (64px) ====================== -->
  <header class="topbar" role="banner">
    <div class="brand">
      <!--
        AFFICHER / MASQUER LA NAVIGATION. Le bouton existe aux DEUX tailles, avec la même
        signification : en compact il ouvre le tiroir, en desktop il masque la colonne (⌘/Ctrl+B).

        Il n'avait jusqu'ici aucune règle de style hors de la media query « ≤ 900 px » : il restait
        donc rendu en desktop, tabulable, et son clic n'y produisait STRICTEMENT RIEN — `.sidebar.open`
        et le voile n'ayant d'effet que sous 900 px. Il a maintenant une fonction des deux côtés.
      -->
      <button
        #menuBtn
        type="button"
        class="menu-btn"
        (click)="basculerNavigation()"
        [attr.aria-label]="navToggleLabel()"
        [title]="navToggleLabel()"
        [attr.aria-expanded]="compact() ? drawerOpen() : sidebarMode() !== 'hidden'"
        aria-controls="nav-laterale"
      >
        <svg lucideIcon="menu" aria-hidden="true"></svg>
      </button>

      <!--
        LOGO OFFICIEL DE LA TUTELLE (demande CCE, 2026-08-12) — repris du portail pm.gov.tn.
        Il porte à lui seul l'emblème, le drapeau ET « الجمهورية التونسية — رئاسة الحكومة » : il
        remplace donc les deux images et la ligne de sous-titre qui les reproduisaient séparément,
        avec des proportions et un espacement qui n'étaient pas ceux de l'original.

        Le titre du CCE passe SOUS le logo : l'organisme se lit sous la tutelle qui l'institue.

        L'`alt` porte le libellé traduit et non un texte décoratif : le sous-titre EXISTE toujours,
        il est simplement dessiné. Un lecteur d'écran doit l'entendre, et la version française reste
        servie par le même identifiant i18n qu'avant.
      -->
      <div class="brand-mark">
        <img
          src="assets/logo-presidence-gouvernement-ar.png"
          class="brand-logo"
          alt="الجمهورية التونسية — رئاسة الحكومة"
          i18n-alt="@@layout.appSubtitle"
        />
        <div class="t1" i18n="@@layout.appTitle">هيئة مراقبي الدولة</div>
      </div>
    </div>

    <!-- Recherche globale (⌘K / Ctrl K / « / ») : rubriques de l'espace courant, voir le composant. -->
    <app-recherche-globale
      class="search"
      role="search"
      [groupes]="rubriquesRecherche()"
      [libelleEspace]="sectionLabel()"
      [placeholder]="uiTexts().searchPlaceholder"
      [espace]="espaceUrl()"
      [utilisateur]="user()?.sub ?? null"
    />

    <div class="topbar-actions">
      <!-- Sélecteur d'espace. Le bouton EST l'indicateur d'espace courant : un seul élément pour savoir
           où l'on se trouve et pour en changer — sans lui, basculer fait changer tout le menu latéral
           sans qu'aucun repère ne dise pourquoi. Masqué sous deux espaces (voir showSpaceSwitcher). -->
      @if (showSpaceSwitcher()) {
        <button
          type="button"
          class="space-trigger"
          popovertarget="space-switcher"
          [attr.aria-label]="uiTexts().spaceSwitcherLabel"
          [title]="uiTexts().spaceSwitcherLabel"
        >
          @if (espaceCourant(); as espace) {
            @if (espace.icon) {
              <svg [lucideIcon]="espace.icon" class="icon" aria-hidden="true"></svg>
            }
            <span class="space-name">{{ navLabel(espace) }}</span>
          }
          <svg lucideIcon="chevron-down" class="chevron" aria-hidden="true"></svg>
        </button>

        <div
          #spaceSwitcher
          id="space-switcher"
          popover="auto"
          class="dropdown-panel space-panel"
          role="dialog"
          [attr.aria-label]="uiTexts().spacesTitle"
        >
          <div class="dropdown-section">
            <div class="section-title">{{ uiTexts().spacesTitle }}</div>
          </div>
          <div class="dropdown-nav">
            @for (espace of espacesAccessibles(); track espace.id) {
              <a
                [routerLink]="espace.route"
                (click)="spaceSwitcher.hidePopover()"
                class="dropdown-item space-item"
                [class.is-current]="espace.id === espaceCourant()?.id"
                [attr.aria-current]="espace.id === espaceCourant()?.id ? 'true' : null"
              >
                @if (espace.icon) {
                  <svg [lucideIcon]="espace.icon" class="icon" aria-hidden="true"></svg>
                }
                <span class="label">{{ navLabel(espace) }}</span>
                @if (espace.id === espaceCourant()?.id) {
                  <!-- L'espace courant se distingue par la couleur ET par une marque lue à voix haute :
                       une différence purement visuelle n'existe pas pour un lecteur d'écran. -->
                  <span class="sr-only">— {{ uiTexts().currentSpaceLabel }}</span>
                }
              </a>
            }
          </div>
        </div>
      }

      <!-- Messagerie فضاء التواصل (demande du 2026-08-06) : un LIEN, pas un flyover — la messagerie a
           son écran, et l'icône y mène. Réservée aux deux espaces qui l'ont (Président ↔ contrôleurs). -->
      @if (showMessagerie()) {
        <a
          class="notification-trigger"
          [routerLink]="messagerieRoute()"
          routerLinkActive="is-active"
          [attr.aria-label]="uiTexts().messagerieLabel"
          [title]="uiTexts().messagerieLabel"
        >
          <svg lucideIcon="message-circle" class="icon" aria-hidden="true"></svg>
          @if (messagesNonLus() > 0) {
            <span class="notification-badge">{{ messagesBadgeText() }}</span>
            <!-- Le nombre doit être ANNONCÉ, pas seulement vu : une pastille muette n'existe pas pour
                 un lecteur d'écran. -->
            <span class="sr-only">{{ messagesNonLus() }} {{ uiTexts().messagerieNonLus }}</span>
          }
        </a>
      }

      <!-- Bouton de notification déclenchant le flyover via le Popover API natif -->
      <button
        type="button"
        class="notification-trigger"
        popovertarget="notifications-drawer"
        [attr.aria-label]="uiTexts().notificationLabel"
      >
        <svg lucideIcon="bell" class="icon" aria-hidden="true"></svg>
        @if (unreadCount() > 0) {
          <span class="notification-badge">{{ unreadCount() }}</span>
        }
      </button>

      <!-- Bouton de profil déclenchant le dropdown via le Popover API natif -->
      <button
        type="button"
        class="profile-trigger"
        popovertarget="user-dropdown"
        [attr.aria-label]="uiTexts().userMenuLabel"
      >
        <div class="avatar" aria-hidden="true">
          {{ userInitials() }}
        </div>
      </button>

      <!-- Le tiroir PARTAGÉ (shared/ui/slide-over) : le même que la messagerie de mission. -->
      <app-slide-over
        #notificationsDrawer
        panelId="notifications-drawer"
        panelClass="notifications-panel"
        [titre]="uiTexts().notifications"
        [corpsNu]="true"
      >
        @if (unreadCount() > 0) {
          <span slideOverTitleExtra class="unread-pill">{{ unreadCount() }} {{ lang() === 'ar' ? 'جديد' : 'neuves' }}</span>
        }

        <div slideOverActions class="drawer-actions-bar">
          @if (notifications().length > 0) {
            <button type="button" class="action-link" (click)="markAllAsRead()">
              <svg lucideIcon="check" class="icon-mini" aria-hidden="true"></svg>
              {{ uiTexts().markAllAsRead }}
            </button>
            <button type="button" class="action-link is-clear" (click)="clearAll()">
              <svg lucideIcon="x" class="icon-mini" aria-hidden="true"></svg>
              {{ uiTexts().clearAll }}
            </button>
            <!-- Regroupement (CCE 2026-09-16) : par famille métier, ou fil par date. Le bouton dit le mode
                 vers lequel il bascule — pas celui en cours, qui se voit déjà à l'écran. -->
            <button
              type="button"
              class="action-link is-mode"
              (click)="toggleNotifGroupMode()"
              [attr.aria-label]="uiTexts().groupModeLabel"
              [attr.aria-pressed]="notifGroupMode() === 'famille'"
            >
              <svg [lucideIcon]="notifGroupMode() === 'famille' ? 'calendar' : 'layers'" class="icon-mini" aria-hidden="true"></svg>
              {{ notifGroupMode() === 'famille' ? uiTexts().groupByDate : uiTexts().groupByFamille }}
            </button>
          } @else {
            <span class="no-unread-msg">{{ uiTexts().noUnread }}</span>
          }
        </div>

        @if (notifError()) {
          <div slideOverActions class="notifications-error" role="alert">
            <span>{{ uiTexts().notificationsError }}</span>
            <button type="button" class="action-link" (click)="retryNotifications()">{{ uiTexts().retry }}</button>
          </div>
        }

        <div class="notifications-body">
          @if (notifications().length > 0) {
            @for (g of notificationGroups(); track g.def.code) {
              <section class="notif-group" [class.is-collapsed]="notifGroupMode() === 'famille' && estRepliee(g.def.code)">
                @if (notifGroupMode() === 'famille') {
                  <button
                    type="button"
                    class="notif-group-head"
                    (click)="toggleNotifGroupe(g.def.code)"
                    [attr.aria-expanded]="!estRepliee(g.def.code)"
                  >
                    <svg [lucideIcon]="g.def.icon" class="icon-mini" aria-hidden="true"></svg>
                    <span class="notif-group-title">{{ lang() === 'ar' ? g.def.ar : g.def.fr }}</span>
                    <span class="notif-group-count">{{ g.items.length }}</span>
                    @if (g.unread > 0) {
                      <span class="unread-pill">{{ g.unread }} {{ uiTexts().unreadShort }}</span>
                    }
                    <svg [lucideIcon]="estRepliee(g.def.code) ? 'chevron-down' : 'chevron-up'" class="icon-mini notif-group-chevron" aria-hidden="true"></svg>
                  </button>
                }
                @if (notifGroupMode() === 'date' || !estRepliee(g.def.code)) {
            <div class="notifications-list">
              @for (item of g.items; track item.id) {
                <div 
                  class="notification-card" 
                  [class.is-unread]="item.unread"
                  [class.is-warning]="item.type === 'warning'"
                  [class.is-info]="item.type === 'info'"
                  [class.is-success]="item.type === 'success'"
                  [class.is-danger]="item.type === 'danger'"
                  (click)="openNotification(item)"
                >
                  <div class="card-icon-wrapper">
                    @if (item.type === 'warning') {
                      <svg lucideIcon="alert-triangle" class="card-icon" aria-hidden="true"></svg>
                    } @else if (item.type === 'success') {
                      <svg lucideIcon="check" class="card-icon" aria-hidden="true"></svg>
                    } @else if (item.type === 'danger') {
                      <svg lucideIcon="bell-ring" class="card-icon" aria-hidden="true"></svg>
                    } @else {
                      <svg lucideIcon="info" class="card-icon" aria-hidden="true"></svg>
                    }
                  </div>
                  <div class="card-content">
                    <div class="card-title-row">
                      <span class="card-title">{{ lang() === 'ar' ? item.titleAr : item.titleFr }}</span>
                      @if (item.unread) {
                        <span class="unread-dot-indicator"></span>
                      }
                    </div>
                    <p class="card-desc">{{ lang() === 'ar' ? item.descAr : item.descFr }}</p>
                    <span class="card-time">{{ lang() === 'ar' ? item.timeAr : item.timeFr }}</span>
                  </div>
                  <button 
                    type="button" 
                    class="card-remove-btn" 
                    (click)="removeNotification(item.id, $event)"
                    [attr.aria-label]="lang() === 'ar' ? 'إخفاء' : 'Masquer'"
                  >
                    <svg lucideIcon="x" class="icon-tiny" aria-hidden="true"></svg>
                  </button>
                </div>
              }
            </div>
                }
              </section>
            }
          } @else {
            <div class="empty-state">
              <div class="empty-icon-circle">
                <svg lucideIcon="bell" class="empty-icon" aria-hidden="true"></svg>
              </div>
              <h3 class="empty-title">{{ uiTexts().notificationsEmpty }}</h3>
              <p class="empty-desc">{{ uiTexts().notificationsEmptyDesc }}</p>
            </div>
          }
        </div>

        <div slideOverFooter class="notifications-footer">
          <!-- La page « كل الإشعارات », commune à tous les espaces (elle n'existait pas avant le 2026-09-16). -->
          <a [routerLink]="notificationsViewAllLink" (click)="notificationsDrawer.hide()" class="footer-link">
            {{ uiTexts().viewAll }}
          </a>
        </div>
      </app-slide-over>

      <!-- Dropdown utilisateur utilisant Popover API natif et CSS Anchor Positioning -->
      <div
        #dropdown
        id="user-dropdown"
        popover="auto"
        class="dropdown-panel"
        role="dialog"
        [attr.aria-label]="uiTexts().userMenuLabel"
      >
        <!-- En-tête : Informations de l'utilisateur connecté -->
        <div class="dropdown-header">
          <div class="header-avatar" aria-hidden="true">
            {{ userInitials() }}
          </div>
          <div class="user-info">
            <span class="user-name">{{ userName() }}</span>
            <span class="user-email">{{ userEmail() }}</span>
          </div>
        </div>

        <div class="dropdown-divider"></div>

        <!-- Section Navigation -->
        <div class="dropdown-nav">
          <a
            routerLink="/controleur/profil"
            (click)="dropdown.hidePopover()"
            class="dropdown-item"
          >
            <svg lucideIcon="user" class="icon" aria-hidden="true"></svg>
            <span class="label">{{ uiTexts().profile }}</span>
          </a>
        </div>

        <div class="dropdown-divider"></div>

        <!-- Section Paramètres régionaux (LangSwitcher) -->
        <div class="dropdown-section">
          <div class="section-title">{{ uiTexts().language }}</div>
          <div class="lang-switch-menu" role="group" aria-label="Langue" i18n-aria-label="@@layout.language">
            <button
              type="button"
              [class.active]="lang() === 'ar'"
              [attr.aria-pressed]="lang() === 'ar'"
              (click)="setLang('ar')"
            >
              AR
            </button>
            <button
              type="button"
              [class.active]="lang() === 'fr'"
              [attr.aria-pressed]="lang() === 'fr'"
              (click)="setLang('fr')"
            >
              FR
            </button>
          </div>
        </div>

        <div class="dropdown-divider"></div>

        <!-- Action de déconnexion -->
        <div class="dropdown-actions">
          <button
            type="button"
            (click)="logout(); dropdown.hidePopover()"
            class="dropdown-item is-logout"
          >
            <svg lucideIcon="log-out" class="icon" aria-hidden="true"></svg>
            <span class="label">{{ uiTexts().logout }}</span>
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- Voile du tiroir (compact uniquement) -->
  @if (drawerOpen() && compact()) {
    <div class="drawer-scrim" (click)="closeDrawer()" aria-hidden="true"></div>
  }

  <!-- ====================== MENU LATÉRAL ======================
       Trois états en desktop — déployé (248 px) / rail d'icônes (72 px) / masqué — portés par les
       classes du shell ; tiroir modal sous 900 px. Côté inline-start ⇒ à droite en RTL.

       Ce n'est plus un <aside> : en desktop il n'a pas à ouvrir un point de repère
       « complementary » qui encadrerait inutilement le point de repère « navigation » qu'il
       contient déjà ; en compact il doit devenir un `dialog`, rôle qu'un <aside> ne peut pas
       porter en même temps qu'une navigation.
  -->
  <div
    #sidebarEl
    id="nav-laterale"
    class="sidebar"
    [class.open]="drawerOpen()"
    [attr.role]="compact() ? 'dialog' : null"
    [attr.aria-modal]="compact() && drawerOpen() ? 'true' : null"
    [attr.aria-label]="compact() ? sectionLabel() : null"
    [attr.inert]="navInerte() ? '' : null"
    [cdkTrapFocus]="compact() && drawerOpen()"
  >
    <!-- Navigation groupée : chaque groupe = un espace (en-tête optionnel) + ses étapes/écrans. -->
    <nav class="sidebar-nav" [attr.aria-label]="sectionLabel()">
    @for (group of navGroups(); track group.id) {
      @if (groupLabel(group); as gl) {
        <!-- En rail, l'en-tête se réduit à un filet : le texte reste dans le DOM (donc lu), seule sa
             hauteur tombe à zéro. Un `display:none` l'aurait retiré aux lecteurs d'écran aussi. -->
        <div class="nav-group-label">{{ gl }}</div>
      } @else if (!$first) {
        <!-- Groupe SANS titre après un autre groupe : un filet le sépare, sinon ses entrées paraissent
             appartenir au groupe précédent (défaut relevé par le CCE le 2026-09-18 sur l'a posteriori). -->
        <div class="nav-group-sep" aria-hidden="true"></div>
      }
      @for (item of group.items; track item.id) {
        @if (item.separeAvant) {
          <!-- Filet sans titre entre deux grappes d'un même groupe (décision Q11b, 2026-09-29). -->
          <div class="nav-group-sep nav-group-sep--interne" aria-hidden="true"></div>
        }
        @if (item.route) {
          <!-- `queryParams: 'exact'` : plusieurs entrées peuvent mener au même écran avec un
               `etape` différent (missions). Sans cette option, elles seraient TOUTES actives à la
               fois. `queryAgnostic` inverse le réglage pour une entrée-chapeau dont les étapes sont
               des onglets dans la page — voir l'interface NavItem. -->
          <a
            class="nav-item"
            [routerLink]="item.route"
            [queryParams]="item.query ?? null"
            routerLinkActive
            #rla="routerLinkActive"
            [class.active]="(rla.isActive || actifParOnglet(item)) && !eteinte(item)"
            [routerLinkActiveOptions]="{
              queryParams: item.queryAgnostic && !estEclipsee(item) ? 'ignored' : 'exact',
              paths: cedeLeChemin(item) ? 'exact' : 'subset'
            }"
            (click)="closeDrawer()"
            [cceTooltip]="navLabel(item)"
            [cceTooltipActif]="estEnRail()"
          >
            @if (item.icon) {
              <svg [lucideIcon]="item.icon" class="icon" aria-hidden="true"></svg>
            } @else {
              <span class="dot" aria-hidden="true"></span>
            }
            <span class="label">{{ navLabel(item) }}</span>
            @if (badgeCount(item); as n) {
              <span class="nav-badge" [attr.aria-label]="n + (lang() === 'ar' ? ' في انتظار إجراء' : ' en attente')">{{ badgeText(item) }}</span>
            }
          </a>
        } @else {
          <!-- Ecran non encore implemente (placeholder). -->
          <span
            class="nav-item is-disabled"
            aria-disabled="true"
            [cceTooltip]="navLabel(item)"
            [cceTooltipActif]="estEnRail()"
          >
            @if (item.icon) {
              <svg [lucideIcon]="item.icon" class="icon" aria-hidden="true"></svg>
            } @else {
              <span class="dot" aria-hidden="true"></span>
            }
            <span class="label">{{ navLabel(item) }}</span>
          </span>
        }
      }
    }
    </nav>

    <!--
      PIED DU MENU — DÉPLOYÉ ⇄ RAIL. Le second des deux contrôles : le hamburger dit « montre / cache »,
      ce chevron dit « large / étroit ». Deux gestes distincts plutôt qu'un bouton unique qui ferait
      défiler trois états, où l'on ne sait jamais où l'on atterrit.

      Absent en compact : dans un tiroir qui se superpose au contenu, réduire à 72 px ne rendrait de
      place à personne.
    -->
    @if (!compact()) {
      <div class="sidebar-footer">
        <button
          type="button"
          class="rail-toggle"
          (click)="basculerRail()"
          [attr.aria-label]="railToggleLabel()"
          [title]="railToggleLabel()"
          [attr.aria-expanded]="sidebarMode() !== 'rail'"
          aria-controls="nav-laterale"
        >
          <svg [lucideIcon]="chevronRepli()" class="icon" aria-hidden="true"></svg>
          <span class="label">{{ railToggleLabel() }}</span>
        </button>
      </div>
    }
  </div>

  <!-- ====================== CONTENU ====================== -->
  <main id="main-content" class="main" tabindex="-1">
    <router-outlet />
  </main>
</div>

<!-- Compte suspendu dans le SI (403 COMPTE_SUSPENDU, 2026-10-20) : rien d'autre n'est utilisable, l'écran le dit et
     n'offre que la déconnexion — complète, Keycloak compris (logout). -->
@if (compteSuspendu()) {
  <div class="compte-suspendu" role="alertdialog" aria-modal="true" aria-labelledby="cs-titre" aria-describedby="cs-texte"
       cdkTrapFocus [cdkTrapFocusAutoCapture]="true" data-testid="compte-suspendu">
    <div class="cs-carte">
      <h2 id="cs-titre">{{ uiTexts().suspenduTitre }}</h2>
      <p id="cs-texte">{{ uiTexts().suspenduTexte }}</p>
      <button type="button" class="cs-btn" (click)="logout()">{{ uiTexts().logout }}</button>
    </div>
  </div>
}
